import type {
  Media,
  OnboardingApplicationHydrated,
} from '../../../lib/flotiq-client/types/api-response';

import { use, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { getEnv } from '../../../lib/env';
import { getTestProps, getTranslatedField } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import LinkButton from '../../../components/LinkButton/LinkButton';

import { ModalInstanceContext } from '../../../contexts/ModalContext';

import { NewTabIcon } from '../../../images/shapes';

const frameworkCategories = [
  'framework',
  'styling',
  'content',
  'deployment',
] as const;

const attributesCategories = [
  'features',
  'content_type_definitions',
  'plugins',
] as const;

const PopulateSpaceTemplateDetailsContent = ({
  application,
  testId,
}: {
  application: OnboardingApplicationHydrated;
  testId?: string;
}) => {
  const { t, i18n } = useTranslation();
  const modalInstance = use(ModalInstanceContext);

  const dataApiUrl = getEnv('VITE_FLOTIQ_ONBOARDING_APPS_CTD_URL')?.split(
    '/api/v1/content',
  )?.[0];

  const handleUseTemplate = useCallback(() => {
    modalInstance?.resolve?.(application);
  }, [modalInstance, application]);

  return (
    <>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 h-full flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-y-2 gap-x-4">
            <h1
              className="text-2xl md:text-3xl/[140%]"
              {...getTestProps(testId, 'name')}
            >
              {t('PopulateSpaceModal.AppDetails.Template', {
                name: getTranslatedField(application, 'name', i18n.language),
              })}
            </h1>
            <a
              className="cursor-pointer text-blue underline hover:text-blue-600 flex items-center 
                     gap-2 font-medium sm:px-3 md:py-2"
              href={getTranslatedField(
                application,
                'github_link',
                i18n.language,
              )}
              target="_blank"
              rel="noreferrer"
              {...getTestProps(testId, 'source-code-link')}
            >
              <NewTabIcon />
              {t('PopulateSpaceModal.AppDetails.SourceCode')}
            </a>
          </div>
          <p className="md:text-lg ">
            {getTranslatedField(application, 'description', i18n.language)}
          </p>

          <div className="grid md:grid-cols-3 gap-4 md:gap-0">
            {attributesCategories.map((category) => {
              const attributes = getTranslatedField(
                application,
                category,
                i18n.language,
              );
              if (!attributes?.length) {
                return null;
              }
              return (
                <div key={category} className="space-y-4 md:p-4">
                  <h2 className="text-lg font-bold">
                    {t(`PopulateSpaceModal.AppDetails.${category}`)}
                  </h2>
                  <ol className="list-disc list-inside flex flex-col gap-1">
                    {attributes.map((attribute: string, index: number) => (
                      <li key={`${attribute}-${index}`} className="pl-2">
                        {attribute}
                      </li>
                    ))}
                  </ol>
                </div>
              );
            })}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 md:py-8 flex flex-col gap-7">
          {frameworkCategories.map((category) => {
            const logos = getTranslatedField(
              application,
              category,
              i18n.language,
            );
            if (!logos?.length) {
              return null;
            }
            return (
              <div key={category} className="space-y-2">
                <h2 className="text-lg font-bold">
                  {t(`PopulateSpaceModal.AppDetails.${category}`)}
                </h2>
                <div className="flex items-center gap-3 h-10">
                  {logos.map((logo: Media) => (
                    <div key={logo.id} className="flex items-center gap-3">
                      <img
                        src={`${dataApiUrl}/${logo.url}`}
                        className="max-h-10"
                        alt={logo.alt || logo.fileName}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {!!application.gallery?.length && (
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          {getTranslatedField(application, 'gallery', i18n.language)?.map(
            (image: Media) => (
              <img
                key={image.id}
                src={`${dataApiUrl}/${image.url}`}
                className="w-full aspect-video object-cover rounded-lg"
                alt={image.alt || image.fileName}
              />
            ),
          )}
        </div>
      )}

      <div
        className="w-full absolute bottom-0 left-0 flex mt-4 items-center justify-center py-4 px-8 space-x-3
                  border-t dark:border-slate-700 bg-slate-50 dark:bg-slate-900"
      >
        <Button
          buttonColor="blue"
          onClick={handleUseTemplate}
          {...getTestProps(testId, 'use-template', 'testId')}
        >
          {t('PopulateSpaceModal.AppDetails.UseThisTemplate')}
        </Button>

        <LinkButton
          link={getTranslatedField(application, 'preview_link', i18n.language)}
          buttonColor="blueBordered"
          iconImage={<NewTabIcon />}
          iconPosition="start"
          additionalIconClasses="w-4"
          target="_blank"
          rel="noreferrer"
          {...getTestProps(testId, 'preview-link', 'testId')}
        >
          {t('PopulateSpaceModal.AppDetails.Preview')}
        </LinkButton>
      </div>
    </>
  );
};

export default PopulateSpaceTemplateDetailsContent;
